import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import {
  User as UserIcon,
  Receipt,
  KeyRound,
  Loader2,
  Save,
  Languages,
  ShieldCheck,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { SUPPORTED_LANGUAGES, getStoredLanguage, setStoredLanguage } from "@/i18n";
import PasswordInput from "@/components/ui/PasswordInput";

const useTabs = () => {
  const { t } = useTranslation();
  return [
    { key: "personal", label: t("profile.tab_personal"), icon: UserIcon },
    { key: "billing", label: t("profile.tab_billing", "Billing Information"), icon: Receipt },
    { key: "password", label: t("profile.tab_password"), icon: KeyRound },
    { key: "preferences", label: t("profile.tab_preferences"), icon: Languages },
  ];
};

const inputCls =
  "flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

export default function Profile() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const tabs = useTabs();
  // Cash verifier accounts can't change their own password — admin manages it.
  const isVerifier = user?.role === "CASH_VERIFIER";
  const visibleTabs = isVerifier ? tabs.filter((t) => t.key !== "password") : tabs;
  const [tab, setTab] = useState(visibleTabs[0]?.key || "personal");

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("profile.title")}</div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {user?.name} {user?.last_name || ""}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t("profile.account_number")} <span className="font-mono font-semibold text-slate-700">{user?.agent_code || user?.username}</span>
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 px-3 flex flex-wrap gap-1 overflow-x-auto">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`profile-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                  active ? "text-[#0F62FE]" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                {active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#0F62FE] rounded-full" />}
              </button>
            );
          })}
        </div>
        <div className="p-6">
          {tab === "personal" && <PersonalTab user={user} onSaved={refresh} />}
          {tab === "billing" && <BillingTab user={user} onSaved={refresh} />}
          {tab === "password" && <PasswordTab />}
          {tab === "preferences" && <PreferencesTab />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PersonalTab({ user, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    last_name: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    address: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",
      state: user.state || "",
      city: user.city || "",
      address: user.address || "",
    });
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/profile", form);
      toast.success(t("profile.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="profile-personal">
      <Field label={t("profile.account_number")}>
        <input value={user?.agent_code || user?.username || ""} disabled className={inputCls + " bg-slate-50 text-slate-500 font-mono"} />
      </Field>
      <Field label={t("profile.name")}>
        <input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("profile.last_name")}>
        <input data-testid="profile-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("profile.email")}>
        <input data-testid="profile-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("profile.phone")}>
        <input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("profile.state")}>
        <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("profile.city")}>
        <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("profile.address")} full>
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
      </Field>
      <div className="sm:col-span-2">
        <button
          data-testid="profile-personal-save"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {busy ? t("profile.saving") : t("profile.save")}
        </button>
      </div>
    </div>
  );
}

function BillingTab({ user, onSaved }) {
  const [form, setForm] = useState({
    billing_name: "",
    billing_country: "",
    billing_state: "",
    billing_city: "",
    billing_address: "",
    billing_zip: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      billing_name: user.billing_name || "",
      billing_country: user.billing_country || "",
      billing_state: user.billing_state || "",
      billing_city: user.billing_city || "",
      billing_address: user.billing_address || "",
      billing_zip: user.billing_zip || "",
    });
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/me/billing", form);
      toast.success("Billing info saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="profile-billing">
      <Field label="Billing name" full>
        <input data-testid="billing-name" value={form.billing_name} onChange={(e) => setForm({ ...form, billing_name: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Billing country">
        <input value={form.billing_country} onChange={(e) => setForm({ ...form, billing_country: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Billing state">
        <input value={form.billing_state} onChange={(e) => setForm({ ...form, billing_state: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Billing city">
        <input value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Billing zip code">
        <input value={form.billing_zip} onChange={(e) => setForm({ ...form, billing_zip: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Billing address" full>
        <input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className={inputCls} />
      </Field>
      <div className="sm:col-span-2">
        <button
          data-testid="profile-billing-save"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save billing info
        </button>
      </div>
    </div>
  );
}

function CardsTab() {
  const [cards, setCards] = useState([]);
  const [provider, setProvider] = useState("mock");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ number: "", holder_name: "", expiry_month: "", expiry_year: "", cvv: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/cards"), api.get("/payments/provider-info")]);
      setCards(c.data);
      setProvider(p.data.provider);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addCard = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/cards", {
        number: form.number,
        holder_name: form.holder_name,
        expiry_month: Number(form.expiry_month),
        expiry_year: Number(form.expiry_year),
        cvv: form.cvv,
      });
      toast.success("Card added");
      setShowAdd(false);
      setForm({ number: "", holder_name: "", expiry_month: "", expiry_year: "", cvv: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add card");
    } finally { setBusy(false); }
  };

  const removeCard = async (id) => {
    if (!window.confirm("Remove this card?")) return;
    try {
      await api.delete(`/cards/${id}`);
      toast.success("Card removed");
      load();
    } catch (e) {
      toast.error("Could not remove card");
    }
  };

  return (
    <div className="space-y-4" data-testid="profile-cards">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Saved cards</h3>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            Cards are tokenized via the
            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[10px]">{provider}</span>
            provider. We never store your full card number or CVV.
          </p>
        </div>
        <button
          data-testid="cards-add-btn"
          onClick={() => setShowAdd((v) => !v)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6]"
        >
          <Plus className="w-4 h-4" /> Add card
        </button>
      </div>

      {showAdd && (
        <form onSubmit={addCard} className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="cards-add-form">
          <Field label="Card holder name" full>
            <input data-testid="card-holder" required value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Card number" full>
            <input data-testid="card-number" required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className={inputCls} placeholder="4242 4242 4242 4242" inputMode="numeric" autoComplete="cc-number" />
          </Field>
          <Field label="Expiry month (MM)">
            <input data-testid="card-exp-month" required type="number" min="1" max="12" value={form.expiry_month} onChange={(e) => setForm({ ...form, expiry_month: e.target.value })} className={inputCls} placeholder="12" />
          </Field>
          <Field label="Expiry year (YYYY)">
            <input data-testid="card-exp-year" required type="number" min="2025" max="2099" value={form.expiry_year} onChange={(e) => setForm({ ...form, expiry_year: e.target.value })} className={inputCls} placeholder="2030" />
          </Field>
          <Field label="CVV (not stored)">
            <input data-testid="card-cvv" required value={form.cvv} onChange={(e) => setForm({ ...form, cvv: e.target.value })} className={inputCls} placeholder="123" inputMode="numeric" autoComplete="cc-csc" />
          </Field>
          <div className="sm:col-span-2 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-white">Cancel</button>
            <button data-testid="card-submit" type="submit" disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Tokenize & save
            </button>
          </div>
        </form>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="cards-table">
          <thead>
            <tr>
              {["Holder", "Brand", "Last 4", "Expires", "Status", ""].map((h) => (
                <th key={h} className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && cards.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No saved cards yet.</td></tr>
            )}
            {cards.map((c) => (
              <tr key={c.id} className="border-b border-slate-100" data-testid={`card-row-${c.last4}`}>
                <td className="px-4 py-3 text-slate-700">{c.holder_name}</td>
                <td className="px-4 py-3 text-slate-700 font-semibold">{c.brand}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">•••• {c.last4}</td>
                <td className="px-4 py-3 text-slate-700">{String(c.expiry_month).padStart(2, "0")} / {c.expiry_year}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold tracking-wider">{c.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`card-remove-${c.last4}`} onClick={() => removeCard(c.id)} className="text-rose-600 hover:text-rose-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PasswordTab() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  // Live checklist that mirrors the backend policy 1:1. Rules only tick
  // when the user has typed at least one character so an empty field
  // doesn't spuriously look "clean".
  const rules = useMemo(
    () => [
      {
        key: "length",
        label: t("profile.pw_rule_length", { defaultValue: "At least 8 characters" }),
        test: (p) => (p || "").length >= 8,
      },
      {
        key: "letter",
        label: t("profile.pw_rule_letter", { defaultValue: "At least one letter" }),
        test: (p) => /[A-Za-z]/.test(p || ""),
      },
      {
        key: "digit",
        label: t("profile.pw_rule_digit", { defaultValue: "At least one number" }),
        test: (p) => /[0-9]/.test(p || ""),
      },
      {
        key: "no_edge_space",
        label: t("profile.pw_rule_no_edge_space", {
          defaultValue: "No spaces at the start or end",
        }),
        test: (p) => !!p && p === p.trim(),
      },
    ],
    [t],
  );
  const rulesState = rules.map((r) => ({ ...r, ok: r.test(form.new_password) }));
  const allRulesOk = rulesState.every((r) => r.ok);
  const passwordsMatch = form.new_password.length > 0 && form.new_password === form.confirm;
  const isSame = form.current_password.length > 0 && form.current_password === form.new_password;
  const submitDisabled =
    busy || !form.current_password || !allRulesOk || !passwordsMatch || isSame;

  const save = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;
    setBusy(true);
    try {
      await api.post("/me/password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(
        t("profile.password_changed_signout", {
          defaultValue:
            "Password updated. All sessions and trusted devices were revoked — please sign in again with your new password.",
        }),
      );
      setForm({ current_password: "", new_password: "", confirm: "" });
      // Give the toast a moment to render before we sign the user out.
      setTimeout(() => {
        try { logout(); } catch { /* logout is best-effort — we still redirect */ }
        navigate("/login", { replace: true });
      }, 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-md space-y-4" data-testid="profile-password">
      <Field label={t("profile.current_password")}>
        <PasswordInput
          data-testid="pwd-current"
          required
          value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          className={inputCls}
          autoComplete="current-password"
        />
      </Field>
      <Field label={t("profile.new_password")}>
        <PasswordInput
          data-testid="pwd-new"
          required
          minLength={8}
          value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          className={inputCls}
          autoComplete="new-password"
        />
      </Field>
      <ul
        className="grid gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
        data-testid="pwd-rules"
      >
        {rulesState.map((r) => (
          <li
            key={r.key}
            data-testid={`pwd-rule-${r.key}`}
            data-ok={r.ok ? "true" : "false"}
            className={
              "flex items-center gap-2 text-xs " +
              (r.ok ? "text-emerald-700" : "text-slate-500")
            }
          >
            {r.ok ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-slate-300" />
            )}
            {r.label}
          </li>
        ))}
      </ul>
      <Field label={t("profile.confirm_password")}>
        <PasswordInput
          data-testid="pwd-confirm"
          required
          minLength={8}
          value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          className={inputCls}
          autoComplete="new-password"
          aria-invalid={form.confirm.length > 0 && !passwordsMatch ? true : undefined}
        />
      </Field>
      {form.confirm.length > 0 && !passwordsMatch && (
        <div className="text-xs text-rose-600" data-testid="pwd-mismatch">
          {t("profile.passwords_dont_match", { defaultValue: "Passwords do not match." })}
        </div>
      )}
      {isSame && (
        <div className="text-xs text-rose-600" data-testid="pwd-same">
          {t("profile.pw_same_as_current", {
            defaultValue: "New password must be different from the current one.",
          })}
        </div>
      )}
      <button
        data-testid="pwd-save"
        type="submit"
        disabled={submitDisabled}
        className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
        {busy ? t("profile.changing_password") : t("profile.change_password")}
      </button>
    </form>
  );
}


function PreferencesTab() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  // Prefer the language stored on the user's account (server-side) so it
  // sticks across devices; fall back to the locally-cached choice.
  const [selected, setSelected] = useState(user?.language || getStoredLanguage());
  const [busy, setBusy] = useState(false);
  const dirty = selected !== (user?.language || getStoredLanguage());

  useEffect(() => {
    if (user?.language) setSelected(user.language);
  }, [user?.language]);

  const save = async () => {
    setBusy(true);
    try {
      // Persist to the account so future logins & other devices see it.
      await api.put("/me/profile", { language: selected });
      // Apply immediately across the entire UI (i18n + localStorage).
      setStoredLanguage(selected);
      // Refresh the auth user so `user.language` matches the new value.
      await refresh();
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl" data-testid="profile-preferences">
      <div className="flex items-center gap-2 mb-1">
        <Languages className="w-4 h-4 text-[#0F62FE]" />
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("settings.preferences")}
        </div>
      </div>
      <h2
        className="text-xl font-bold text-slate-900"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {t("settings.language")}
      </h2>
      <p className="text-slate-500 mt-1 text-sm">{t("settings.language_hint")}</p>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-language-picker">
        {SUPPORTED_LANGUAGES.map((l) => {
          const active = l.code === selected;
          return (
            <button
              key={l.code}
              onClick={() => setSelected(l.code)}
              data-testid={`profile-lang-${l.code}`}
              className={
                "inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-colors " +
                (active
                  ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                  : "bg-white text-slate-700 border-slate-300 hover:border-[#0F62FE] hover:text-[#0F62FE]")
              }
            >
              <span className="text-[10px] uppercase tracking-widest opacity-80">{l.code}</span>
              {l.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <button
          data-testid="profile-preferences-save"
          onClick={save}
          disabled={busy || !dirty}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {t("common.save", "Save")}
        </button>
      </div>
    </div>
  );
}
